import { useEffect, useState } from 'react'
import type { JSX } from 'react'
import { useSearchParams } from 'react-router'
import type { Schema } from '@/api/client'
import type { Query } from '@/api/query'
import { useBrand } from '@/brand/context'
import { PropertyPicker } from '@/settings/google-properties/PropertyPicker'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

/**
 * Google Search Console — the second card in S-D3's Connections panel (S-B22a).
 *
 * **Its own component rather than more of `ConnectionsPanel`**, because the two
 * connections share a shape and nothing else. LinkedIn's grant is an access token
 * with no refresh and a sixty-day life, so that card's whole job is to prompt a
 * reconnect before it dies. Google's is a refresh token that outlives everything,
 * so this card's job is different: say which property each site resolved to, and
 * make the *"we connected and found nothing of yours"* case obvious rather than
 * leaving somebody staring at an empty dashboard wondering why.
 *
 * **No risk acknowledgement here, and that is deliberate.** LinkedIn's checkbox
 * exists because automating a member's account can get it banned — a risk they
 * have to accept in their own name (§8). Reading your own Search Console data
 * carries no such risk, Google's own consent screen is the disclosure, and
 * inventing a second checkbox would make the LinkedIn one read as boilerplate.
 *
 * **The connection is read by the panel and handed down, rather than fetched
 * here.** It was this card's own `useQuery` until S-G13 put a GA4 card in the
 * same panel over the same grant. Two cards each reading
 * `GET /connections/google` is not merely a duplicated request: pressing
 * Disconnect here refetches *this* card's copy and leaves the other one still
 * rendering a grant that no longer exists. One query, one truth, and `refetch`
 * below now updates both.
 *
 * The three states every component ships (CLAUDE.md, Frontend): loading while the
 * connection is read, an error state with a retry, and an empty state written as
 * an invitation — which is what a freshly provisioned tenant actually sees.
 *
 * **Each site's line is where its property is changed** since the settings
 * consolidation's second batch (2026-09-15). *Change* opens the picker under
 * that line — one at a time, as `SettingRow` panels do — and choosing closes it
 * and re-reads the connection, whose `sites` this list is drawn from.
 */

/** Mirrors the outcomes `search_console_callback` redirects with. */
const OUTCOMES: Record<string, { tone: 'success' | 'danger'; title: string; body: string }> = {
  connected: {
    tone: 'success',
    title: 'Search Console connected',
    body: 'How your site performs in Google search can now be read.',
  },
  no_access: {
    tone: 'danger',
    title: 'Connected, but no matching property',
    body:
      'That Google account has no Search Console property for your site. Sign in with a ' +
      'different account, or ask whoever manages the site to give you access.',
  },
  failed: {
    tone: 'danger',
    title: 'Search Console did not finish connecting',
    body:
      'Nothing was changed. This usually means the consent screen was cancelled, or it was ' +
      'left open too long. Try again — it takes a few seconds.',
  },
}

type GoogleConnection = Schema<'SearchConsoleConnectionOut'>

export function SearchConsoleCard({
  connection,
}: {
  connection: Query<GoogleConnection>
}): JSX.Element {
  const brand = useBrand()
  const toast = useToast()
  const { membership } = useActiveTenant()
  // `discovery:manage` is what `PUT /seo/sites/{id}/property` is gated on.
  const editable = may(membership, 'discovery:manage')
  const [params, setParams] = useSearchParams()
  const [picking, setPicking] = useState<string | null>(null)

  // The callback redirects back here with `?search_console=…`, because a redirect
  // from Google carries no body of ours. Read once, shown as a toast, then
  // stripped from the URL — a reload should not re-announce a connection that
  // happened ten minutes ago.
  const outcome = params.get('search_console')
  useEffect(() => {
    if (outcome === null) return
    const message = OUTCOMES[outcome]
    if (message !== undefined) {
      toast.show({ kind: message.tone, title: message.title, body: message.body })
    }
    params.delete('search_console')
    setParams(params, { replace: true })
    connection.refetch()
    // `outcome` is the only input that should re-run this; re-running it on a
    // refetched connection would show the toast again on every poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outcome])

  const data = connection.state.data
  const sites = data?.sites ?? []
  const matched = sites.filter((site) => site.gsc_property !== null && site.gsc_property !== '')

  return (
    <section className="ar-card" id="search-console-properties">
      <div className="ar-card__header">
        <h2>Search Console properties</h2>
        <span className="ar-card__spacer" />
        {/*
          SHELL-55 moved *connected or not* to the row above, so the badge that
          is left is the one this card is actually about: whether the connected
          account resolved to a property for any of the tenant's sites. That was
          always the more useful of the two — a connection that matched nothing
          is the state somebody stares at an empty dashboard over.
        */}
        {data?.connected === true && matched.length === 0 ? (
          <span className="ar-badge ar-badge--warning">⚠ No matching property</span>
        ) : null}
      </div>

      {/* The inset, same as the two cards above it — see `ConnectionsPanel`. */}
      <div className="ar-card__body">
        <p className="ar-text-body-sm">
          Which Search Console property each of your sites reads from. {brand.productName} is{' '}
          <strong>read-only</strong> here — it never submits a sitemap, never changes a property,
          never writes to Analytics, and never touches Ads, Gmail or Drive.
        </p>

        {connection.state.status === 'loading' ? (
          <Loading label="Loading your Search Console connection" />
        ) : connection.state.status === 'error' ? (
          /*
            A sentence, not a second `ErrorState`. This card and the Connections
            row above it read one query, so a failure used to draw two error
            panels with two *Try again* buttons for one request — and pressing
            either refetched the same thing. The row above owns the recovery
            because it owns the connection; this says where it is.
          */
          <p className="ar-empty ar-empty--inline">
            Your Google connection could not be read, so there is nothing to map your sites against.
            The Connections list above has the retry.
          </p>
        ) : data === null ? (
          <p className="ar-empty ar-empty--inline">
            We could not read your Search Console connection just now. Reload the page to try again.
          </p>
        ) : (
          <>
            {data.connected ? (
              <>
                <p className="ar-text-body-sm">
                  Reading as{' '}
                  <strong>{data.account_label ?? 'the Google account you authorised'}</strong>.
                </p>
                {sites.length > 0 ? (
                  <ul
                    className="ar-text-body-sm ar-stack ar-stack--tight"
                    style={{ margin: 0, paddingLeft: '1.25rem' }}
                  >
                    {sites.map((site) => (
                      <li key={site.id}>
                        <strong>{site.domain}</strong>{' '}
                        {site.gsc_property !== null && site.gsc_property !== '' ? (
                          <span className="ar-mono">{site.gsc_property}</span>
                        ) : (
                          <span className="ar-muted">— no property yet.</span>
                        )}{' '}
                        {editable && picking !== site.id ? (
                          <Button
                            size="sm"
                            tone="ghost"
                            aria-label={`Change the Search Console property for ${site.domain}`}
                            onClick={() => {
                              setPicking(site.id)
                            }}
                          >
                            {site.gsc_property !== null && site.gsc_property !== ''
                              ? 'Change'
                              : 'Choose'}
                          </Button>
                        ) : null}
                        {picking === site.id ? (
                          <div
                            className="ar-stack ar-stack--tight"
                            style={{ marginTop: 'var(--ar-space-2)' }}
                          >
                            <PropertyPicker
                              siteId={site.id}
                              current={site.gsc_property ?? null}
                              onChosen={() => {
                                setPicking(null)
                                connection.refetch()
                                toast.show({
                                  kind: 'success',
                                  title: 'Property updated',
                                  body: `${site.domain} now reads from the property you chose.`,
                                })
                              }}
                            />
                            <div>
                              <Button
                                size="sm"
                                tone="quiet"
                                onClick={() => {
                                  setPicking(null)
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {!data.can_verify_sites ? (
                  <p className="ar-callout ar-text-body-sm" data-tone="warning">
                    You did not grant the site-ownership permission, so {brand.productName} cannot
                    show you a DNS record to prove you own a site Google does not know about yet.
                    Reconnect in the Connections list above and accept every permission Google
                    offers if you need it.
                  </p>
                ) : null}
              </>
            ) : (
              <p className="ar-empty ar-empty--inline">
                {data.status === null
                  ? 'Search Console is not connected, so your SEO dashboard has no ranking or click data in it yet.'
                  : data.status === 'pending'
                    ? 'A connection was started and never finished. Connect again — it takes a few seconds.'
                    : 'Google no longer accepts this connection. Connect again to keep reading your search data.'}
              </p>
            )}
            {data.detail !== null ? (
              <p className="ar-text-body-sm ar-muted">Google said: {data.detail}</p>
            ) : null}
          </>
        )}
      </div>
    </section>
  )
}
